import { create } from 'zustand';
import { createTradeUpdatesEventSource } from '../services/api';
import { globalEventDeduplicator } from '../services/globalEventDeduplicator';
import { createCacheFreshnessDetector, CacheFreshnessDetector } from '../services/cacheFreshnessDetector';
import { QueryClient } from '@tanstack/react-query';
import { getUserActivityTracker } from '../hooks/useUserActivityTracking';

interface SSEState {
  // Connection state
  isConnected: boolean;
  eventSource: EventSource | null;
  connectionAttempts: number;
  lastConnectedAt: Date | null;
  lastConnectionAttempt: number | null; // Track last connection attempt timestamp
  isOptimisticConnection: boolean; // Track if we're in optimistic mode
  connectionValidationTimeout: NodeJS.Timeout | null;
  clientId: string | null; // Store SSE client ID for activity tracking
  
  // 🚀 NEW: Position snapshot coordination
  lastReconnectionTimestamp: number | null; // Track reconnection for deduplication
  
  // Connection management
  connect: (accessToken: string) => void;
  disconnect: () => void;
  setConnectionStatus: (connected: boolean) => void;
  setClientId: (clientId: string | null) => void;
  
  // Event handlers
  eventHandlers: Set<(event: MessageEvent) => void>;
  addEventHandler: (handler: (event: MessageEvent) => void) => void;
  removeEventHandler: (handler: (event: MessageEvent) => void) => void;
  
  // Deduplication control
  resetDeduplication: () => void;
  getDeduplicationStats: () => any;
  
  // Cache freshness management
  cacheFreshnessDetector: CacheFreshnessDetector | null;
  initializeCacheFreshness: (queryClient: QueryClient) => void;
  getCacheFreshnessReport: (timezone?: string) => any;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_INTERVAL = 3000;

// 🚀 COORDINATION: Position snapshot deduplication constants
const RECONNECTION_SNAPSHOT_WINDOW = 10000; // 10 seconds

let reconnectTimeoutId: NodeJS.Timeout | null = null;

// Function to boost activity on SSE reconnection
const triggerActivityBoostOnReconnection = () => {
  try {
    const tracker = getUserActivityTracker();
    
    if (tracker) {
      console.log('🚀 [SSE Recovery] Triggering activity boost on reconnection');
      tracker.recordReconnectionActivity();
    }
  } catch (error) {
    console.log('Activity boost on reconnection failed:', error);
  }
};

// 🚀 NEW: Enhanced position snapshot coordination
const shouldSkipPositionSnapshot = (reason: string): boolean => {
  const lastReconnection = localStorage.getItem('lastReconnectionTimestamp');
  if (!lastReconnection) return false;
  
  const timeSinceReconnection = Date.now() - parseInt(lastReconnection);
  
  if (timeSinceReconnection < RECONNECTION_SNAPSHOT_WINDOW) {
    console.log(`📸 [Coordination] Skipping ${reason} snapshot - reconnection ${Math.round(timeSinceReconnection/1000)}s ago`);
    return true;
  }
  
  return false;
};

// 🔥 NEW: Cache-aware recovery function
const performCacheAwareRecovery = async (accessToken: string) => {
  try {
    console.log('🔄 Starting cache-aware recovery...');
    
    // Calculate disconnection duration
    const lastConnected = localStorage.getItem('lastSSEConnection');
    const disconnectDuration = lastConnected 
      ? Date.now() - new Date(lastConnected).getTime()
      : 300000; // Default to 5 minutes if unknown
    
    console.log(`📊 Disconnect duration: ${Math.round(disconnectDuration / 1000)}s`);
    
    // 1. Record cache invalidation timestamp before invalidating
    localStorage.setItem('lastCacheInvalidation', new Date().toISOString());
    
    // 2. Record cache invalidation on backend to coordinate snapshots
    try {
      const response = await fetch('/api/polling/sse/record-cache-invalidation', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Cache invalidation recorded:', result);
      } else {
        console.warn('⚠️ Failed to record cache invalidation:', response.status);
      }
    } catch (error) {
      console.error('❌ Error recording cache invalidation:', error);
    }
    
    // 3. 🚀 ENHANCED: Smart position snapshot coordination
    // Check multiple factors to prevent duplicate snapshots
    const now = Date.now();
    const lastSnapshotTime = localStorage.getItem('lastPositionSnapshotRequest');
    const timeSinceLastSnapshot = now - (lastSnapshotTime ? parseInt(lastSnapshotTime) : 0);
    
    // Skip if recent manual request OR if this is a reconnection that might trigger backend snapshot
    const skipDueToRecentRequest = timeSinceLastSnapshot <= 2000;
    const skipDueToReconnectionCoordination = shouldSkipPositionSnapshot('cache_recovery');
    
    if (!skipDueToRecentRequest && !skipDueToReconnectionCoordination) {
      localStorage.setItem('lastPositionSnapshotRequest', now.toString());
      window.dispatchEvent(new CustomEvent('requestPositionSnapshot', {
        detail: { source: 'reconnection_recovery', timestamp: now }
      }));
      console.log('📸 [SSE Recovery] Requesting coordinated position snapshot');
    } else {
      const reason = skipDueToRecentRequest ? 'recent request' : 'reconnection coordination';
      console.log(`📸 [SSE Recovery] Skipping position snapshot - ${reason}`);
    }
    
    // NEW: Force position cache invalidation for longer disconnections
    if (disconnectDuration > 60000) { // 1 minute
      console.log('🔄 [SSE Recovery] Long disconnection detected, forcing position refresh');
      // Trigger comprehensive position recovery with source tracking
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('requestPositionSnapshot', {
          detail: { source: 'long_disconnect_recovery', timestamp: Date.now() }
        }));
      }, 5000); // Delayed secondary check
    }
    
    // 4. Smart cache invalidation based on disconnection duration
    // Note: This will be handled by the component that has access to queryClient
    window.dispatchEvent(new CustomEvent('performSmartCacheInvalidation', {
      detail: { 
        disconnectDuration,
        context: {
          deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          userType: 'active' // Could be determined from user behavior
        }
      }
    }));
    
    // 5. Delayed comprehensive recovery to avoid race condition
    setTimeout(() => {
      requestDelayedComprehensiveRecovery(accessToken);
    }, 3000); // Wait for cache invalidation queries to complete
    
  } catch (error) {
    console.error('❌ Cache-aware recovery failed:', error);
    // Fallback to basic recovery
    window.dispatchEvent(new CustomEvent('requestPositionSnapshot'));
  }
};

// 🔥 NEW: Request delayed comprehensive recovery
const requestDelayedComprehensiveRecovery = async (accessToken: string) => {
  try {
    console.log('📸 Requesting delayed comprehensive recovery...');
    
    const response = await fetch('/api/polling/sse/recovery-sync', {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('✅ Comprehensive recovery completed:', result);
    } else {
      console.warn('⚠️ Failed to request comprehensive recovery:', response.status);
    }
  } catch (error) {
    console.error('❌ Delayed recovery request failed:', error);
  }
};

export const useSSEStore = create<SSEState>((set, get) => ({
  // Initial state - Start optimistic
  isConnected: true, // 🟢 OPTIMISTIC: Assume connected until proven otherwise
  eventSource: null,
  connectionAttempts: 0,
  lastConnectedAt: null,
  lastConnectionAttempt: null, // Track last connection attempt timestamp
  eventHandlers: new Set(),
  cacheFreshnessDetector: null,
  isOptimisticConnection: true,
  connectionValidationTimeout: null,
  clientId: null, // Initialize client ID as null
  lastReconnectionTimestamp: null, // 🚀 COORDINATION: Initialize reconnection tracking

  // Connection management
  connect: (accessToken: string) => {
    const state = get();
    
    // Don't create multiple connections, but allow upgrade from optimistic to real
    if (state.eventSource && state.eventSource.readyState === EventSource.OPEN && !state.isOptimisticConnection) {
      console.log('🔌 SSE already connected with real connection, skipping');
      return;
    }

    // Prevent rapid reconnections by checking if we just attempted a connection
    const now = Date.now();
    if (state.lastConnectionAttempt && (now - state.lastConnectionAttempt) < 500) {
      console.log('🔌 SSE connection attempt too recent, skipping to prevent spam');
      return;
    }

    // Close any existing connection and clear timeouts
    if (state.eventSource) {
      console.log('🔌 Closing existing SSE connection to establish real connection');
      state.eventSource.close();
    }
    
    if (state.connectionValidationTimeout) {
      clearTimeout(state.connectionValidationTimeout);
    }

    // Clear any pending reconnection
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }

    // Set the connection attempt timestamp to prevent rapid reconnections
    set({ lastConnectionAttempt: now });

    // If this is an upgrade from optimistic, keep connected state but mark as transitioning
    if (!state.isOptimisticConnection) {
      // Set optimistic connection state only if not already connected
      console.log('🟢 Starting optimistic SSE connection...');
      set({ 
        isConnected: true,
        isOptimisticConnection: true
      });
    } else {
      console.log('🔄 Upgrading optimistic SSE connection to real connection...');
    }
    
    // Set validation timeout to switch to fallback if connection fails
    const validationTimeout = setTimeout(() => {
      const currentState = get();
      if (currentState.isOptimisticConnection && currentState.eventSource) {
        // Check if we have a real connection (OPEN) or it's still trying to connect (CONNECTING)
        const readyState = currentState.eventSource.readyState;
        const hasValidConnection = readyState === EventSource.OPEN || readyState === EventSource.CONNECTING;
        
        if (!hasValidConnection) {
          console.log('⚠️ SSE connection validation failed - switching to fallback mode');
          set({ 
            isConnected: false,
            isOptimisticConnection: false 
          });
        } else if (readyState === EventSource.OPEN) {
          console.log('✅ SSE connection validation passed - real connection confirmed');
          set({ 
            isOptimisticConnection: false // No longer optimistic - it's confirmed real
          });
        } else {
          console.log('🔄 SSE connection still establishing - extending validation timeout');
          // Connection is still establishing, extend timeout
          const extendedTimeout = setTimeout(() => {
            const finalState = get();
            if (finalState.isOptimisticConnection && finalState.eventSource) {
              if (finalState.eventSource.readyState === EventSource.OPEN) {
                console.log('✅ SSE connection established after extended timeout');
                set({ isOptimisticConnection: false });
              } else {
                console.log('❌ SSE connection failed to establish after extended timeout');
                set({ isConnected: false, isOptimisticConnection: false });
              }
            }
          }, 10000); // Additional 10 seconds for slow connections
          
          set({ connectionValidationTimeout: extendedTimeout });
        }
      }
    }, 5000); // 5 second initial validation window

    set({ connectionValidationTimeout: validationTimeout });

    try {
      console.log('🔌 Establishing global SSE connection...', { 
        hasToken: !!accessToken, 
        isOptimistic: get().isOptimisticConnection 
      });
      
      const eventSource = createTradeUpdatesEventSource(accessToken);
      
      console.log('📡 EventSource created, setting up listeners...', {
        readyState: eventSource.readyState,
        url: eventSource.url
      });
      
      // Set up event listeners
      eventSource.onopen = () => {
        console.log('✅ Global SSE connection established - optimistic assumption confirmed');
        const currentState = get();
        
        // 🔥 NEW: Track connection timestamp for smart cache invalidation
        localStorage.setItem('lastSSEConnection', new Date().toISOString());
        
        // 🚀 COORDINATION: Track reconnection timestamp for position snapshot deduplication
        const reconnectionTimestamp = Date.now();
        localStorage.setItem('lastReconnectionTimestamp', reconnectionTimestamp.toString());
        
        // Clear validation timeout since connection succeeded
        if (currentState.connectionValidationTimeout) {
          clearTimeout(currentState.connectionValidationTimeout);
        }
        
        set({ 
          isConnected: true,
          isOptimisticConnection: false, // No longer optimistic - it's real
          connectionAttempts: 0,
          lastConnectedAt: new Date(),
          connectionValidationTimeout: null,
          lastReconnectionTimestamp: reconnectionTimestamp
        });
        
        // 🚀 MOBILE RECOVERY: Boost activity on SSE reconnection
        triggerActivityBoostOnReconnection();
        
        // 🔥 NEW: Cache-aware recovery on reconnection
        performCacheAwareRecovery(accessToken);
        
        // Dispatch global connection event
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('sseConnected', {
            detail: { timestamp: new Date().toISOString() }
          }));
        }
      };

      eventSource.onmessage = (event: MessageEvent) => {
        try {
          // Parse event data for deduplication check
          const eventData = JSON.parse(event.data);
          
          // 🔄 GLOBAL DEDUPLICATION - CRITICAL INTEGRATION POINT
          // Check if this event should be skipped as a duplicate
          const shouldSkip = globalEventDeduplicator.shouldSkipEvent(eventData);
          
          if (shouldSkip) {
            // Event is a duplicate - skip processing by all handlers
            console.log(`🔄 [SSE Store] Skipping duplicate event: ${eventData.type}`);
            return;
          }
          
          // 🧠 SMART CACHE-AWARE PROCESSING
          // Get cache freshness information for intelligent decisions
          const state = get();
          const cacheFreshnessDetector = state.cacheFreshnessDetector;
          
          if (cacheFreshnessDetector && eventData.type === 'metrics_calculated') {
            // For metrics_calculated events, check if we should process based on cache state
            const shouldAllow = cacheFreshnessDetector.shouldAllowMetricsEvent();
            
            if (!shouldAllow) {
              console.log(`🧠 [SSE Store] Skipping metrics_calculated event - cache is fresh`);
              return;
            } else {
              console.log(`🧠 [SSE Store] Processing metrics_calculated event - cache needs update`);
            }
          }
          
          // Event is not a duplicate and passes cache freshness checks - forward to all registered handlers
          console.log(`✅ [SSE Store] Processing event: ${eventData.type}`);
          
          // 🔔 Dispatch custom DOM events for position-related events
          // This allows components to listen to specific event types without registering handlers
          if (eventData.type === 'position_snapshot') {
            window.dispatchEvent(new CustomEvent('positionSnapshot', { 
              detail: eventData.data 
            }));
          } else if (eventData.type === 'open_position_opened') {
            window.dispatchEvent(new CustomEvent('openPositionOpened', { 
              detail: eventData.data 
            }));
          } else if (eventData.type === 'open_position_closed') {
            window.dispatchEvent(new CustomEvent('openPositionClosed', { 
              detail: eventData.data 
            }));
          } else if (eventData.type === 'open_position_pnl_change') {
            window.dispatchEvent(new CustomEvent('openPositionPnlChange', { 
              detail: eventData.data 
            }));
          } else if (eventData.type === 'open_position_modified') {
            window.dispatchEvent(new CustomEvent('openPositionModified', { 
              detail: eventData.data 
            }));
          } else if (eventData.type === 'open_position_milestone') {
            window.dispatchEvent(new CustomEvent('openPositionMilestone', { 
              detail: eventData.data 
            }));
          } else if (eventData.type === 'open_positions_refresh') {
            window.dispatchEvent(new CustomEvent('openPositionsRefresh', { 
              detail: eventData.data 
            }));
          }
          
          // Forward to all registered handlers
          const handlers = get().eventHandlers;
          handlers.forEach(handler => {
            try {
              handler(event);
            } catch (error) {
              console.error('❌ Error in SSE event handler:', error);
            }
          });
        } catch (parseError) {
          console.error('❌ Error parsing SSE event:', parseError);
          
          // Even if parsing fails, forward to handlers for their own error handling
          const handlers = get().eventHandlers;
          handlers.forEach(handler => {
            try {
              handler(event);
            } catch (error) {
              console.error('❌ Error in SSE event handler during error recovery:', error);
            }
          });
        }
      };

      eventSource.onerror = (error: Event) => {
        console.error('❌ Global SSE connection error:', error);
        const currentState = get();
        
        // Clear validation timeout
        if (currentState.connectionValidationTimeout) {
          clearTimeout(currentState.connectionValidationTimeout);
        }
        
        set({ 
          isConnected: false,
          isOptimisticConnection: false,
          connectionValidationTimeout: null
        });
        
        // Dispatch global disconnection event
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('sseDisconnected', {
            detail: { timestamp: new Date().toISOString(), error }
          }));
        }
        
        // Attempt reconnection if we haven't exceeded max attempts
        if (currentState.connectionAttempts < MAX_RECONNECT_ATTEMPTS) {
          const newAttempts = currentState.connectionAttempts + 1;
          set({ connectionAttempts: newAttempts });
          
          console.log(`🔄 Reconnecting global SSE (attempt ${newAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutId = setTimeout(() => {
            const latestState = get();
            if (latestState.eventSource) {
              latestState.eventSource.close();
            }
            set({ eventSource: null });
            get().connect(accessToken);
          }, RECONNECT_INTERVAL);
        } else {
          console.error('❌ Global SSE max reconnection attempts reached, giving up');
          if (currentState.eventSource) {
            currentState.eventSource.close();
          }
          set({ eventSource: null });
        }
      };

      set({ eventSource });
      
    } catch (error) {
      console.error('❌ Failed to create global SSE connection:', error);
      set({ isConnected: false });
    }
  },

  disconnect: () => {
    const state = get();
    
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
    
    if (state.connectionValidationTimeout) {
      clearTimeout(state.connectionValidationTimeout);
    }
    
    if (state.eventSource) {
      console.log('🔌 Closing global SSE connection...');
      state.eventSource.close();
    }
    
    set({ 
      isConnected: false, 
      eventSource: null,
      connectionAttempts: 0,
      isOptimisticConnection: false,
      connectionValidationTimeout: null
    });
  },

  setConnectionStatus: (connected: boolean) => {
    set({ isConnected: connected });
  },

  setClientId: (clientId: string | null) => {
    set({ clientId });
    console.log(`📱 SSE Client ID updated:`, clientId);
  },

  // Event handler management
  addEventHandler: (handler: (event: MessageEvent) => void) => {
    const state = get();
    const newHandlers = new Set(state.eventHandlers);
    newHandlers.add(handler);
    set({ eventHandlers: newHandlers });
    console.log(`📡 Added SSE event handler (${newHandlers.size} total handlers)`);
  },

  removeEventHandler: (handler: (event: MessageEvent) => void) => {
    const state = get();
    const newHandlers = new Set(state.eventHandlers);
    newHandlers.delete(handler);
    set({ eventHandlers: newHandlers });
    console.log(`📡 Removed SSE event handler (${newHandlers.size} total handlers)`);
  },

  // Deduplication control
  resetDeduplication: () => {
    console.log('🔄 [SSE Store] Resetting global event deduplication');
    globalEventDeduplicator.resetForNewSession();
  },

  getDeduplicationStats: () => {
    return globalEventDeduplicator.getMemoryStats();
  },

  // Cache freshness management
  initializeCacheFreshness: (queryClient: QueryClient) => {
    const detector = createCacheFreshnessDetector(queryClient);
    set({ cacheFreshnessDetector: detector });
    console.log('🧠 [SSE Store] Cache freshness detector initialized');
  },

  getCacheFreshnessReport: (timezone = 'UTC') => {
    const detector = get().cacheFreshnessDetector;
    if (!detector) {
      console.warn('⚠️ [SSE Store] Cache freshness detector not initialized');
      return null;
    }
    return detector.getFreshnessReport(timezone);
  }
}));